import {
  BORDER_RADIUS,
  boxShadowButton,
  label,
  lightRed,
  orange,
  orangeHovered,
  primary,
  primaryText,
  secondary,
  TOGGLE_GROUP_BACKGROUND,
  TRANSITION_BACKGROUND,
  white,
} from 'styles'

import { createTheme } from '@mui/material/styles'

declare module '@mui/material/styles' {
  interface Theme {
    colors: {
      primaryColor: string
      primaryLightColor: string
      primaryDarkColor: string
      secondaryColor: string
      secondaryLightColor: string
      secondaryDarkColor: string
      primaryTextColor: string
      secondaryTextColor: string
    }
  }

  // allow configuration using `createTheme`
  interface ThemeOptions {
    colors?: {
      primaryColor?: string
      primaryLightColor?: string
      primaryDarkColor?: string
      secondaryColor?: string
      secondaryLightColor?: string
      secondaryDarkColor?: string
      primaryTextColor?: string
      secondaryTextColor?: string
    }
  }
}

const theme = createTheme({
  colors: {
    primaryColor: '#14253c',
    primaryLightColor: '#3e4d67',
    primaryDarkColor: '#000017',
    secondaryColor: orange,
    secondaryLightColor: '#ff983f',
    secondaryDarkColor: '#be3500',
    primaryTextColor: '#ffffff',
    secondaryTextColor: '#ffffff',
  },

  palette: {
    error: {
      dark: lightRed,
      light: lightRed,
      main: lightRed,
    },
    primary: {
      main: orange,
    },
    secondary: {
      main: label,
    },
    text: {
      primary: primaryText,
    },
  },

  typography: {
    fontFamily: `"Open Sans", -apple-system, sans-serif`,
  },

  // Global components overrides
  components: {
    MuiAccordion: {
      styleOverrides: {
        rounded: {
          '&:last-of-type': {
            borderRadius: BORDER_RADIUS,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        expandIconWrapper: {
          alignSelf: 'center',
          color: primary,
        },
        root: {
          paddingLeft: '0.5rem',
          paddingRight: '0.5rem',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        endAdornment: {
          top: 'calc(50% - 1.2rem)',
        },
        option: {
          '&[role="option"]': {
            minHeight: '3rem',
          },
        },
        popupIndicator: {
          color: orange,

          '& .MuiSvgIcon-root': {
            height: '2rem',
            width: '2rem',
          },
        },
        root: {
          '& .MuiOutlinedInput-root': {
            paddingBottom: '0.25rem',
            paddingTop: '0.25rem',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            backgroundColor: '#e0e0e0',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: BORDER_RADIUS,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '1.5rem 2rem',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          background: '#FCFBFC',
          borderBottom: '1px solid #EAEBEF',
          borderTop: '1px solid #EAEBEF',
          color: secondary,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          '&&+.MuiDialogContent-root': {
            padding: '2rem 2rem 3.5rem 2rem',
          },
          color: secondary,
          fontSize: '1.2rem',
          fontWeight: '600',
        },
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        row: {
          '& .MuiFormControlLabel-root': {
            marginLeft: '-0.5rem',
            marginRight: '1.2rem',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: '0.5rem',
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          '&::before': {
            borderBottom: `1px solid ${label}`,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        asterisk: {
          color: orange,
        },
        root: {
          color: label,
          fontWeight: '600',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          padding: '1rem 1.5rem',
        },
        notchedOutline: {
          border: `1px solid ${orange}`,
          borderRadius: BORDER_RADIUS,
        },

        root: {
          '&&:hover > fieldset': {
            borderWidth: '2px',
          },
          '&&.Mui-error:hover > fieldset': {
            borderColor: lightRed,
          },
          '&&:not(.Mui-error):hover > fieldset': {
            borderColor: orange,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#C2C2C2',
          padding: '0.5rem',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: BORDER_RADIUS,
          color: primary,
          fontSize: '1rem',
          fontWeight: '600',
          padding: '0.25rem 1rem',
          textTransform: 'inherit',
          transition: TRANSITION_BACKGROUND,

          '&.Mui-selected': {
            background: orange,
            color: white,
          },
          '&.Mui-selected:hover': {
            background: orangeHovered,
            color: white,
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          background: TOGGLE_GROUP_BACKGROUND,
          borderRadius: BORDER_RADIUS,
          boxShadow: boxShadowButton,
          padding: '0.1rem',
        },
        grouped: {
          border: 'none',

          '&:not(.APPLY_TO_ALL)': {
            borderRadius: BORDER_RADIUS,
          },
        },
      },
    },
  },
})

export default theme
